/**
 * Created by Daniel Wang on 1/15/2024.
 * Description: Component for add/edit etl tasks
 * ------ maintenance history ------
 */

import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { UntypedFormControl } from '@angular/forms';
import { AppState } from '../../../../redux';
import * as _lodash from 'lodash';

import { SlideSheetService } from '../../../../services/slide-sheet.service';
import { SlideSheetActionTypes } from '../../../slide-sheet/slide-sheet.model';
import { ETLOperationType, ETLTask, ETLTaskProperty } from '../../../../tamalelibs/models/admin-page.model';
import { AdminPagesService } from '../../../../tamalelibs/services/admin-page.service';
import { AdminPageActionTypes } from '../../../../redux/actions/admin-page.actions';
import { adminSharedPageConstants } from '../../admin-page-setting.constants';
import { catchError, take } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
    selector: 'tam-etl-tasks-editor',
    templateUrl: './etl-tasks-editor.component.html',
    styleUrls: ['./etl-tasks-editor.component.scss']
})
export class ETLTasksEditorComponent implements OnInit, OnDestroy {
    @Input() data?: any;

    // #region public properties
    etlTask = new ETLTask();
    etlOperationTypeItems = Object.values(ETLOperationType);
    isEdit = false;
    name = new UntypedFormControl('');
    selectedETLOperationType = ETLOperationType.ETLNullOperation;
    title = 'New ETL Task';
    // #endregion

    // #region private properties
    // #endregion

    // #region constructor
    constructor(
        private _adminPageService: AdminPagesService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _store: Store<AppState>,
        private _slideSheetService: SlideSheetService
    ) { }
    // #endregion

    // #region angular functions
    ngOnInit() {
        // edit data
        if (this.data) {
            this.isEdit = true;
            this.etlTask = _lodash.cloneDeep(this.data);
            this.title = 'Edit ' + this.etlTask.name;
            this.selectedETLOperationType = this.etlTask.className;
        } else {
            // init controls
            this._initControls();
            this._setETLTaskProperties();
        }
    }

    ngOnDestroy(): void {
    }
    // #endregion

    // #region public functions
    onChangeETLOperationType(event) {
        this.selectedETLOperationType = event;
        this.etlTask.className = this.selectedETLOperationType;
        this._setETLTaskProperties();
    }

    onSlideSheetClose() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CLOSE
        });
    }

    /** Check the length of name. When the length of the name is valid, dispatches action to the store to create/edit object. */
    onSubmitClick() {
        this.name.setErrors(null);

        if (!this.name.value) {
            this.name.setErrors({
                required: true
            });
        } else if (this.name.value.length > 128) {
            this.name.setErrors({
                maxlength: true
            });
        } else {
            const data = {
                taskName: this.etlTask.name,
                className: this.etlTask.className,
                isDefault: this.etlTask.isDefault,
                tssETLTaskPropertyList: this.etlTask.tssETLTaskPropertyList
            };
            if (this.isEdit) {
                data['ID'] = this.etlTask.id;
                this._store.dispatch({
                    type: AdminPageActionTypes.EDIT_SHARED_PAGE,
                    payload: {
                        type: adminSharedPageConstants['etl-tasks'].type,
                        key: 'etlTaskConfig',
                        item: data
                    }
                });

            } else {
                this._adminPageService.checkETLTaskName(this.name.value, this.etlTask.id).pipe(
                    catchError((e: any) => {
                        const mockResult = {
                            result: {
                                message: 'false' // to match server response. boolean will be better.
                            },
                        };
                        return of(mockResult);
                    })
                ).subscribe(result => {
                    if (result?.message === 'false') {
                        this.name.setErrors({
                            nameDuplicated: true
                        });
                    } else {
                        this._store.dispatch({
                            type: AdminPageActionTypes.CREATE_SHARED_PAGE,
                            payload: {
                                type: adminSharedPageConstants['etl-tasks'].type,
                                key: 'etlTaskConfig',
                                item: data
                            }
                        });
                    }
                });
            }
        }
    }
    // #endregion

    // #region private functions
    private _initControls() {
        this.etlTask.className = ETLOperationType.ETLNullOperation;
        this.etlTask.isDefault = false;
    }

    private _setETLTaskProperties() {
        const propertyId = this._adminPageService.findKeyByValue(ETLOperationType, this.selectedETLOperationType);
        this._adminPageService.getETLTaskProperties(propertyId).pipe(
            take(1)
        ).subscribe(res => {
            this.etlTask.tssETLTaskPropertyList = [];
            if (res) {
                res.forEach(item => {
                    this.etlTask.tssETLTaskPropertyList.push(ETLTaskProperty.parse(item));
                });
                this._changeDetectorRef.detectChanges();
            }
        });
    }
    // #endregion
}
